/** Landing page — punchy, high-energy, professional. */

import { Link } from "react-router-dom";
import { Player } from "@remotion/player";
import { HowItWorks } from "@/remotion/compositions/HowItWorks";
import { OverlayShowcase } from "@/remotion/compositions/OverlayShowcase";
import { Button } from "@/components/ui/Button";

export function LandingPage() {
  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0b", color: "#e4e4e7" }}>
      {/* ── Nav ── */}
      <header style={nav}>
        <span style={brandMark}>RALEY</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Link to="/training"><Button variant="ghost" size="sm">Training</Button></Link>
          <Link to="/support"><Button variant="ghost" size="sm">Support</Button></Link>
          <Link to="/login"><Button size="sm">Sign In</Button></Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <section style={{ paddingTop: 120, paddingBottom: 60, textAlign: "center" }}>
        <div style={{ fontSize: 14, color: "#3b82f6", textTransform: "uppercase", letterSpacing: 3, fontFamily: "var(--font-mono)", marginBottom: 16 }}>
          Professional Competition Scoring
        </div>
        <h1 style={{ fontSize: 64, fontWeight: 800, lineHeight: 1.05, textTransform: "uppercase", letterSpacing: 2, maxWidth: 800, margin: "0 auto" }}>
          Score. Broadcast.<br />Compete.
        </h1>
        <p style={{ fontSize: 17, color: "#94a3b8", maxWidth: 500, marginTop: 20, lineHeight: 1.6, margin: "20px auto 0" }}>
          Real-time judging, OBS overlays, and competition management for wakeboard, waterski, and action sport events.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 36, justifyContent: "center" }}>
          <Link to="/login"><Button size="lg">Get Started — Free</Button></Link>
          <a href="#demo"><Button variant="secondary" size="lg">See Demo</Button></a>
        </div>
      </section>

      {/* ── Overlay Showcase — shows the final broadcast output ── */}
      <section style={{ padding: "0 48px 60px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ border: "1px solid #2a2a32", overflow: "hidden", position: "relative" }}>
          <Player
            component={OverlayShowcase}
            compositionWidth={1920}
            compositionHeight={1080}
            durationInFrames={450}
            fps={30}
            style={{ width: "100%", aspectRatio: "16/9" }}
            autoPlay
            loop
            acknowledgeRemotionLicense
            numberOfSharedAudioTags={0}
          />
          <div style={{ position: "absolute", top: 12, right: 16, display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 1, fontFamily: "var(--font-mono)" }}>Live Preview — OBS Output</span>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <span style={{ fontSize: 12, color: "#52525b" }}>What your broadcast looks like with Raley's OBS overlay</span>
        </div>
      </section>

      {/* ── Stats bar ── */}
      <section style={statsBar}>
        {[
          { num: "5", label: "Scoring Models" },
          { num: "10", label: "Max Judges" },
          { num: "0.0", label: "Cost" },
          { num: "<100ms", label: "Score Latency" },
        ].map((s) => (
          <div key={s.label} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: "#fff", fontFamily: "var(--font-mono)" }}>{s.num}</div>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </section>

      {/* ── How it Works ── */}
      <section style={{ padding: "80px 48px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ fontSize: 11, color: "#3b82f6", textTransform: "uppercase", letterSpacing: 3, fontFamily: "var(--font-mono)", marginBottom: 12 }}>How It Works</div>
          <h2 style={{ fontSize: 40, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Three Roles. One Platform.</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
          {[
            { num: "01", title: "Competition Admin", desc: "Create competitions, configure scoring models, invite your team, manage divisions, heats and riders.", color: "#3b82f6" },
            { num: "02", title: "Producer", desc: "Control the live OBS overlay. Focus riders, reveal scores, show leaderboards — all in real time.", color: "#22c55e" },
            { num: "03", title: "Judge", desc: "Score riders from any device. Sliders, quick-buttons, real-time submission. Private until revealed.", color: "#eab308" },
          ].map((r) => (
            <div key={r.num} style={{ background: "#111113", border: "1px solid #2a2a32", padding: 40 }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: r.color, fontFamily: "var(--font-mono)", opacity: 0.3, marginBottom: 16 }}>{r.num}</div>
              <div style={{ fontSize: 18, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>{r.title}</div>
              <div style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.7 }}>{r.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Product Demo Video ── */}
      <section id="demo" style={{ padding: "40px 48px 80px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 11, color: "#22c55e", textTransform: "uppercase", letterSpacing: 3, fontFamily: "var(--font-mono)", marginBottom: 12 }}>Product Demo</div>
          <h2 style={{ fontSize: 40, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>See It In Action</h2>
        </div>
        <div style={{ border: "1px solid #2a2a32", background: "#111113", overflow: "hidden" }}>
          <Player
            component={HowItWorks}
            compositionWidth={1920}
            compositionHeight={1080}
            durationInFrames={5100}
            fps={30}
            style={{ width: "100%", aspectRatio: "16/9" }}
            controls
            acknowledgeRemotionLicense
            numberOfSharedAudioTags={0}
          />
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section style={{ padding: "60px 48px 80px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          {[
            { title: "OBS Browser Source", desc: "1920x1080 transparent overlay. Drop the URL into OBS. Rider cards, scores, focus, leaderboard — all controlled remotely.", accent: "#3b82f6" },
            { title: "5 Scoring Models", desc: "Best Score, Average, Total, Olympic Drop High/Low, Weighted Head Judge. Each explained in plain English when you set up.", accent: "#22c55e" },
            { title: "Configurable Judges", desc: "1 to 10 judges per competition. Invite by email. Auto-assigned judge numbers. Each judge only sees their own scores.", accent: "#eab308" },
            { title: "Account-Level Riders", desc: "Create your rider roster once — names, photos, nicknames, numbers, fun facts. Reuse across every competition.", accent: "#ef4444" },
            { title: "Real-Time WebSocket", desc: "Every score, every focus, every state change broadcasts instantly to all connected screens. No polling, no lag.", accent: "#a855f7" },
            { title: "Training & Certs", desc: "Built-in courses for judges, producers, and competition managers. Video walkthroughs and downloadable PDF certificates.", accent: "#f97316" },
          ].map((f) => (
            <div key={f.title} style={{ background: "#111113", border: "1px solid #2a2a32", padding: 32, display: "flex", gap: 16 }}>
              <div style={{ width: 4, background: f.accent, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{f.title}</div>
                <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.7 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Scoring Models Explainer ── */}
      <section style={{ padding: "60px 48px 80px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 11, color: "#eab308", textTransform: "uppercase", letterSpacing: 3, fontFamily: "var(--font-mono)", marginBottom: 12 }}>Scoring Models</div>
          <h2 style={{ fontSize: 40, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Choose Your System</h2>
          <p style={{ fontSize: 14, color: "#71717a", marginTop: 12 }}>Every model is explained in plain English when you set up your competition. No confusion.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 2 }}>
          {[
            { name: "MAX", desc: "Highest single judge score wins", icon: "↑" },
            { name: "AVG", desc: "Mean of all judge scores", icon: "÷" },
            { name: "SUM", desc: "Total of all scores added", icon: "Σ" },
            { name: "OLYMPIC", desc: "Drop high & low, average rest", icon: "⌀" },
            { name: "WEIGHTED", desc: "Head judge counts 2x", icon: "⚖" },
          ].map((m) => (
            <div key={m.name} style={{ background: "#111113", border: "1px solid #2a2a32", padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 28, color: "#eab308", marginBottom: 12 }}>{m.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{m.name}</div>
              <div style={{ fontSize: 11, color: "#71717a", lineHeight: 1.5 }}>{m.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing ── */}
      <section style={{ padding: "60px 48px 80px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 11, color: "#22c55e", textTransform: "uppercase", letterSpacing: 3, fontFamily: "var(--font-mono)", marginBottom: 12 }}>Pricing</div>
          <h2 style={{ fontSize: 40, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Simple & Fair</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, maxWidth: 900, margin: "0 auto" }}>
          {[
            {
              name: "Starter",
              price: "Free",
              period: "forever",
              features: ["1 competition", "3 judges", "5 riders", "Basic scoring models", "OBS overlay", "Community support"],
              cta: "Get Started",
              accent: false,
            },
            {
              name: "Pro",
              price: "$19",
              period: "/month",
              features: ["Unlimited competitions", "10 judges", "Unlimited riders", "All scoring models", "Named judges", "Training & certs", "Email support"],
              cta: "Start Free Trial",
              accent: true,
            },
            {
              name: "Event",
              price: "$49",
              period: "/event",
              features: ["Everything in Pro", "Priority support", "Custom branding", "Dedicated onboarding", "Invoice billing"],
              cta: "Contact Us",
              accent: false,
            },
          ].map((plan) => (
            <div
              key={plan.name}
              style={{
                background: plan.accent ? "#111113" : "#0a0a0b",
                border: `1px solid ${plan.accent ? "#3b82f6" : "#2a2a32"}`,
                padding: 32,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: plan.accent ? "#3b82f6" : "#71717a" }}>{plan.name}</div>
              <div style={{ marginTop: 16, display: "flex", alignItems: "baseline", gap: 4 }}>
                <span style={{ fontSize: 40, fontWeight: 800, color: "#fff" }}>{plan.price}</span>
                <span style={{ fontSize: 14, color: "#52525b" }}>{plan.period}</span>
              </div>
              <div style={{ marginTop: 24, flex: 1 }}>
                {plan.features.map((f) => (
                  <div key={f} style={{ fontSize: 13, color: "#94a3b8", padding: "6px 0", borderBottom: "1px solid #1a1a1e", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "#22c55e", fontSize: 11 }}>✓</span>
                    {f}
                  </div>
                ))}
              </div>
              <Link to={plan.name === "Event" ? "/support" : "/login"} style={{ textDecoration: "none", marginTop: 24 }}>
                <Button variant={plan.accent ? "primary" : "secondary"} className="w-full">{plan.cta}</Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: "80px 48px", textAlign: "center", borderTop: "1px solid #2a2a32" }}>
        <h2 style={{ fontSize: 48, fontWeight: 800, textTransform: "uppercase", letterSpacing: 2 }}>Ready to Run Your Event?</h2>
        <p style={{ fontSize: 15, color: "#94a3b8", marginTop: 16, maxWidth: 400, margin: "16px auto 0" }}>Free to sign up. Free to use. Set up in minutes.</p>
        <div style={{ marginTop: 36 }}>
          <Link to="/login"><Button size="lg">Get Started</Button></Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ padding: "24px 48px", borderTop: "1px solid #2a2a32", display: "flex", justifyContent: "space-between", fontSize: 12, color: "#52525b" }}>
        <span>&copy; 2026 Koda Software</span>
        <div style={{ display: "flex", gap: 24 }}>
          <Link to="/support" style={{ color: "#52525b" }}>Support</Link>
          <Link to="/training" style={{ color: "#52525b" }}>Training</Link>
          <a href="mailto:raley@koda-software.com" style={{ color: "#52525b", fontFamily: "var(--font-mono)" }}>raley@koda-software.com</a>
        </div>
      </footer>
    </div>
  );
}

const nav: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 48px", height: 56, borderBottom: "1px solid rgba(42,42,50,0.5)", position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: "rgba(10,10,11,0.8)", backdropFilter: "blur(12px)" };
const brandMark: React.CSSProperties = { fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 18, letterSpacing: 3, color: "#e4e4e7" };
const statsBar: React.CSSProperties = { display: "flex", justifyContent: "center", gap: 80, padding: "48px 0", borderBottom: "1px solid #2a2a32", background: "#111113" };
